import { TRANSITION_CANCELED } from '@/features/domaine/machine-etats'
import { type Dispositif, type TypeDemande } from '@/features/domaine/types/opco'
import { useAjouteDemande } from '@/features/interface/demande/ajouter/useAjouteDemande'
import { useDialog } from '@/features/interface/dialog'
import * as api from '@/features/services/demande'
import type { CloneDemandeLaunch } from '../../demande/ajouter/CreationDemandeConfigurations'
import { useFavorisStore } from '../../favoris/favorisStore'
import { useRechercheDemandeStore } from './useRechercheDemandeStore'
import { SilentError } from '@/features/shared/types'
import { demandeToLocation } from '@/features/application/locations'
import {
  navigateTo,
  type NavigateToDemande,
  type NavigateToDemandeExtranet,
  type ToDispositif,
} from '@/router/navigateTo'

export function useRechercheDemandeActions(typeDemande: TypeDemande) {
  const storeDemande = useRechercheDemandeStore()
  const storeAddDemande = useAjouteDemande()
  const { addFavori } = useFavorisStore()

  const dialog = useDialog()

  const ouvrirDemande = (location: { dispositif: Dispositif, sousDispositif: string | null, id: number }) =>
    navigateTo(
      {
        to: 'detail',
        domaine: 'formations',
        module: 'demande',
        dispositif: location.dispositif.toLowerCase() as ToDispositif,
        sousDispositif: location.sousDispositif,
        id: location.id,
      } as NavigateToDemande)


  async function addDemande() {
    try {
      const demande = await storeAddDemande.showAjoute()
      navigateTo({
        to: 'detail',
        domaine: 'formations',
        module: 'demande',
        dispositif: demande.dispositif.toLowerCase() as ToDispositif,
        sousDispositif: 'sousDispositif' in demande ? demande.sousDispositif : null,
        id: demande.id,
      } as NavigateToDemande)
    } catch (err) {
      if (err !== TRANSITION_CANCELED) throw err
      console.error(err)
    }
  }

  async function deleteDemande(id: number, dispositif: Dispositif, sousDispositif?: string | null) {
    if (typeDemande !== 'Formation')
      throw new Error('Suppression non autorisée pour ce type de demande.')

    const result = await dialog.showDialog({
      message: 'Confirmez-vous la suppression du brouillon ?',
      type: 'YesNo',
      title: 'Supprimer un brouillon',
    })
    if (result === 'Yes') {
      const location = demandeToLocation({
        typeDemande: typeDemande,
        dispositif: dispositif,
        origine: 'extranet',
        sousDispositif: sousDispositif,
        id: id
      }) as NavigateToDemandeExtranet
      await api.demandeService.deleteDemande(location)
      await storeDemande.reloadData()
    }
  }

  async function cloneDemande(options: CloneDemandeLaunch) {
    try {
      const demande = await storeAddDemande.showClone(options)
      navigateTo({
        to: 'detail',
        domaine: 'formations',
        module: 'demande',
        dispositif: demande.dispositif.toLowerCase() as ToDispositif,
        sousDispositif: 'sousDispositif' in demande ? demande.sousDispositif : null,
        id: demande.id,
      } as NavigateToDemande)
    } catch (err) {
      if (err === TRANSITION_CANCELED) return
      if (err instanceof SilentError) return
      console.error(err)
    }
  }

  return { ouvrirDemande, deleteDemande, cloneDemande, addFavori, addDemande }
}
